
import { Component, Input, OnInit, ViewChild, ViewContainerRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { DialogContentBase, DialogRef, DialogResult } from '@progress/kendo-angular-dialog';
import { UiNotificationService } from 'src/app/external-ui-shared/services/ui-notification.service';
import { ClaimSubjects } from 'src/app/feature-module/claims-data/models/ClaimSubjects';
import { PortalMessageTemplateDictionaryDataItem } from 'src/app/feature-module/message/models/PortalMessageTemplateDictionaryDataItem';
import { CustomDialogSettings } from 'src/app/shared/model/CustomDialogSettings';
import { CustomDialogService } from 'src/app/shared/services/custom-dialog.service';
import { FormUtilsService } from 'src/app/shared/services/form-utils.service';
import { PortalMessage, PortalMessageFormBuilder } from '../../models/PortalMessage';
import { SendingChannelType } from '../../models/SendingChannelType';
import { PortalMessageService } from '../../service/portal-message.service';
import { PortalMessageTemplatePickerComponent } from '../portal-message-template-picker/portal-message-template-picker.component';
import { PortalMessageRecipient, PortalMessageRecipientFormBuilder } from '../../models/PortalMessageRecipient';
import { RecipientRole } from '../../models/RecipientRole';
import { tap } from 'rxjs';
import { PortalUserService } from 'src/app/feature-module/portal-users/services/portalUser.service';
import { SearchPortalUsersCriteriaFormBuilder } from 'src/app/feature-module/portal-users/models/SearchPortalUsersCriteria';
import { ApplicationPortalType } from 'src/app/shared/model/ApplicationPortalType';

@Component({
  selector: 'app-compose-portal-message',
  templateUrl: './compose-portal-message.component.html',
  styleUrls: ['./compose-portal-message.component.scss']
})
export class ComposePortalMessageComponent extends DialogContentBase implements OnInit {

  constructor(
    public dialog: DialogRef, 
    private fb: UntypedFormBuilder,
      private portalUserService: PortalUserService,
      private formUtilsService: FormUtilsService,
      private uiNotification: UiNotificationService,
      private messageService: PortalMessageService,
      private translateService: TranslateService,
      private customDialogService: CustomDialogService) { 
        super(dialog);
      }

  @Input() claimIdentifier:string;
  @Input() threadId:string;
  @Input() disableTemplates:boolean;
  @Input() disableRecipientSelection:boolean;
  @Input() inputRecipients:PortalMessageRecipient[];
  @Input() applicationPortalTypeId:number = ApplicationPortalType.ClaimCenter;
  @Input() linkedToMessageId = 0;
  @Input() initialTitle = '';

  composeMessageForm: UntypedFormGroup;
  message:PortalMessage;
  claimSubjects : ClaimSubjects;
  sendingChannelType = SendingChannelType.Portal;

  addRecipientForm: UntypedFormGroup;
  contactRecipientHasData:boolean;
  loadingRecipientData:boolean;
  
  allowedRecipientRoles = RecipientRole.RolesForPortalMessage;

  @ViewChild("composePortalMessageDialog", { read: ViewContainerRef })
  public containerRef: ViewContainerRef;

  ngOnInit(): void {
    this.message = PortalMessageFormBuilder.initPortalMessage(this.sendingChannelType, this.threadId);
    this.message.claimIdentifier = this.claimIdentifier
    this.message.applicationPortalTypeId = this.applicationPortalTypeId;
    this.message.linkedToMessageId = this.linkedToMessageId;
    this.message.title = this.initialTitle;
    this.composeMessageForm = PortalMessageFormBuilder.getForm(this.fb, this.message);

    this.addRecipientForm = PortalMessageRecipientFormBuilder.getForm(
      this.fb, 
      PortalMessageRecipientFormBuilder.initPortalMessageRecipient(),this.sendingChannelType);

    if(this.inputRecipients){
        this.inputRecipients.forEach(r => {
          if(!this.isRecipientEmailDuplicate(r.recipientContact)){
            this.composeMessageForm.controls.recipients.value.push(r);
          }
        });
      }       
  }

  send(){

      if(!this.formUtilsService.markFormGroupTouched(this.composeMessageForm)){
        this.uiNotification.showValidationError();
        return;
      }
  
      Object.assign(this.message, this.composeMessageForm.value);

      this.messageService.addMessage(this.message).subscribe(
        () => this.dialog.close({ text: 'OK' })
      );

    }

    chooseTemplate(){

      const dialogSettings: CustomDialogSettings = {
        content: PortalMessageTemplatePickerComponent,
        appendTo: this.containerRef,
        title: this.translateService.instant('claims-center.claimMessages.chooseTemplate'),
        minWidth:'30vw',
        minHeight:'30vh',
        maxWidth:'70vw',
        maxHeight:'70vh',
        dialogInputs: {
          sendingChannelTypeId : this.sendingChannelType,
          claimIdentifier : this.claimIdentifier
        },
        resultCallback: (r: DialogResult) => {
          if(r['template']) {
            const choosenTemplate = r['template'] as PortalMessageTemplateDictionaryDataItem;
            this.message.contentText = choosenTemplate.templateContent;
            this.message.title = choosenTemplate.title;
            this.message.templateId = choosenTemplate.id;
           this.composeMessageForm = PortalMessageFormBuilder.getForm(this.fb, this.message);
           }
        }
      };
  
      this.customDialogService.showDialog(dialogSettings);

    }

    addRecipient(){

      if(!this.formUtilsService.markFormGroupTouched(this.addRecipientForm)){
        this.uiNotification.showValidationError();
        return;
      }

      const addRecipientData = PortalMessageRecipientFormBuilder.initPortalMessageRecipient();

      Object.assign(addRecipientData, this.addRecipientForm.value);

      if(this.isRecipientEmailDuplicate(addRecipientData.recipientContact)){
        this.uiNotification.showWarning('claims-center.claimMessages.sameRecipientMessage');
        return;
      }

      this.composeMessageForm.controls.recipients.value.push(addRecipientData);
    }

    removeRecipient(recipient: PortalMessageRecipient){
      const index = this.composeMessageForm.controls.recipients.value.indexOf(recipient, 0);
      this.composeMessageForm.controls.recipients.value.splice(index,1);
    }

    private isRecipientEmailDuplicate(email: string): boolean{
      if(!email) return false;
      const normalized = email.trim().toLowerCase();
      if(!this.composeMessageForm || !this.composeMessageForm.controls || !this.composeMessageForm.controls.recipients) return false;
      const recipients = this.composeMessageForm.controls.recipients.value as PortalMessageRecipient[];
      if(!recipients || recipients.length === 0) return false;
      return recipients.some(r => r && r.recipientContact && r.recipientContact.trim().toLowerCase() === normalized);
    }

    recipientRoleChanged(roleId:number){
      this.loadingRecipientData = true;
      this.messageService.getRecipientData(this.claimIdentifier, roleId)
      .pipe( tap(() =>  this.loadingRecipientData = false))
      .subscribe(
        (r) =>  {
          
          if(!r.info && roleId != RecipientRole.Other){
            this.contactRecipientHasData = false;

            return;
          }

        const recipient = PortalMessageRecipientFormBuilder.initPortalMessageRecipient();
        recipient.isReadConfirmationRequired = true;
        recipient.recipientContact = r.email;

        if(this.isRecipientEmailDuplicate(recipient.recipientContact)){
          this.uiNotification.showWarning('claims-center.claimMessages.sameRecipientMessage');
          this.contactRecipientHasData = false;
          return;
          }

        recipient.recipientInfo = r.info;
        recipient.portalUserId = r.portalUserId;
        recipient.portalUserName = r.portalUserName;
        recipient.recipientRole = roleId;
        recipient.isDisabledPortalUser = r.isDisabledPortalUser;
        this.addRecipientForm = PortalMessageRecipientFormBuilder.getForm(
          this.fb, recipient,this.sendingChannelType);
          this.contactRecipientHasData = true;
        }
      );

    }
  
    otherUserSelectionChanged(selectedPortalUserId: string){
      const recipient = PortalMessageRecipientFormBuilder.initPortalMessageRecipient();
      recipient.isReadConfirmationRequired = true;
      this.contactRecipientHasData = false;

      if(selectedPortalUserId)
      {

        const searchUserCriteria = SearchPortalUsersCriteriaFormBuilder.initSearchPortalUsersCriteria(false);
        searchUserCriteria.portalUserId = selectedPortalUserId;
        this.portalUserService.searchUsers(searchUserCriteria).subscribe(
         
          (r) => { 
            if(r.length > 0){
              recipient.recipientContact = r[0].normalizedEmail;
              recipient.recipientInfo = `${r[0].name} ${r[0].surname}`;
              recipient.portalUserId = selectedPortalUserId;
              recipient.portalUserName = r[0].userName;
              recipient.recipientRole = RecipientRole.OtherEmployee;
              recipient.isDisabledPortalUser = !r[0].isEnabled;

              if(this.isRecipientEmailDuplicate(recipient.recipientContact)){
                this.uiNotification.showWarning('claims-center.claimMessages.sameRecipientMessage');
                this.contactRecipientHasData = false;
              }
              else{
                this.contactRecipientHasData = true;
                this.addRecipientForm = PortalMessageRecipientFormBuilder.getForm(
                  this.fb, recipient,this.sendingChannelType);
              }
            }
          }
        );
      }
      else{
        this.addRecipientForm = PortalMessageRecipientFormBuilder.getForm(
          this.fb, recipient,this.sendingChannelType);
      }

    }
}
